/**
 * Content Synchronization Service — Wave 22.13.4
 *
 * Host-level content synchronization layer enabling multiple plugins
 * to safely read, modify, and update the same content entity with
 * versioning, conflict resolution, and event propagation.
 *
 * Key design principles:
 * - Host owns version numbers — plugins never set versions directly
 * - Last-writer-wins conflict resolution with optional merge callback
 * - Per-entity subscription model for targeted updates
 * - Full integration with the plugin event bus for cross-cutting concerns
 * - DevTools-inspectable history and subscriber state
 *
 * Usage:
 * ```ts
 * const sync = useContentSyncStore.getState();
 * sync.publish('note-123', '# Hello', 'notes');
 * sync.subscribe('note-123', (update) => { ... });
 * sync.getLatest('note-123');
 * ```
 */

import { create } from 'zustand';
import { usePluginEventBusStore } from './plugin-event-bus';
import { invocationLog } from './interaction-invocation-log';
import { wrapSyncSubscriber } from './sandbox/plugin-execution-wrapper';

// ────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────

/** A versioned content update record. */
export interface ContentVersion {
  entityId: string;
  content: string;
  version: number;
  sourcePlugin: string;
  timestamp: number;
}

/** Result of a publish operation. */
export interface PublishResult {
  accepted: boolean;
  version: number;
  conflict: boolean;
  merged: boolean;
  previousVersion?: number;
}

/** Conflict resolution strategy. */
export type ConflictStrategy = 'last-writer-wins' | 'reject' | 'merge';

/** Conflict details surfaced to subscribers. */
export interface ContentConflict {
  entityId: string;
  localVersion: number;
  incomingVersion: number;
  localContent: string;
  incomingContent: string;
  sourcePlugin: string;
  resolution: ConflictStrategy;
  resolvedContent: string;
  timestamp: number;
}

/** Merge decision record for DevTools inspection. */
export interface MergeDecision {
  entityId: string;
  strategy: ConflictStrategy;
  sourcePlugin: string;
  baseVersion: number;
  resultVersion: number;
  timestamp: number;
  accepted: boolean;
}

/** Handler for content update subscriptions. */
export type ContentUpdateHandler = (update: ContentVersion) => void;

/** Handler for content conflict notifications. */
export type ContentConflictHandler = (conflict: ContentConflict) => void;

/** A subscriber record for DevTools inspection. */
export interface ContentSubscription {
  id: string;
  entityId: string;
  subscriberPlugin: string;
  createdAt: number;
}

/** DevTools snapshot of the content sync state. */
export interface ContentSyncSnapshot {
  entities: Record<string, ContentVersion>;
  subscribers: ContentSubscription[];
  history: ContentVersion[];
  conflicts: ContentConflict[];
  mergeDecisions: MergeDecision[];
}

// ────────────────────────────────────────────────────────
// Constants
// ────────────────────────────────────────────────────────

const MAX_HISTORY_PER_ENTITY = 50;
const MAX_TOTAL_HISTORY = 500;
const MAX_CONFLICTS = 100;
const MAX_MERGE_DECISIONS = 200;

// ────────────────────────────────────────────────────────
// ID Generation
// ────────────────────────────────────────────────────────

let _nextSubId = 1;
function generateSubId(): string {
  return `csub-${Date.now()}-${_nextSubId++}`;
}

// ────────────────────────────────────────────────────────
// Store
// ────────────────────────────────────────────────────────

interface ContentSyncState {
  /** Latest version per entity. */
  entities: Record<string, ContentVersion>;

  /** Per-entity update handlers. */
  _handlers: Record<string, Map<string, { subscriberPlugin: string; handler: ContentUpdateHandler }>>;

  /** Per-entity conflict handlers. */
  _conflictHandlers: Record<string, Map<string, { subscriberPlugin: string; handler: ContentConflictHandler }>>;

  /** Full version history (newest last, capped). */
  history: ContentVersion[];

  /** Conflict history. */
  conflicts: ContentConflict[];

  /** Merge decision log for DevTools. */
  mergeDecisions: MergeDecision[];

  /** Active subscriber metadata (for DevTools). */
  subscriptions: ContentSubscription[];

  /** Default conflict strategy. */
  conflictStrategy: ConflictStrategy;

  // ── Core API ────────────────────────────────────────────

  /**
   * Publish updated content for an entity.
   * Host increments the version number.
   * Broadcasts to all subscribers except the source plugin.
   */
  publish(
    entityId: string,
    content: string,
    sourcePlugin: string,
    options?: {
      /** Expected base version for optimistic concurrency. */
      baseVersion?: number;
      /** Override conflict strategy for this publish. */
      conflictStrategy?: ConflictStrategy;
      /** Custom merge function (only used with 'merge' strategy). */
      mergeFn?: (existing: string, incoming: string) => string;
    },
  ): PublishResult;

  /**
   * Subscribe to content updates for an entity.
   * Returns an unsubscribe function.
   */
  subscribe(
    entityId: string,
    handler: ContentUpdateHandler,
    subscriberPlugin: string,
  ): () => void;

  /**
   * Subscribe to conflict notifications for an entity.
   */
  subscribeConflicts(
    entityId: string,
    handler: ContentConflictHandler,
    subscriberPlugin: string,
  ): () => void;

  /**
   * Get the latest version of an entity, or undefined if not tracked.
   */
  getLatest(entityId: string): ContentVersion | undefined;

  /**
   * Get version history for an entity.
   */
  getHistory(entityId: string): ContentVersion[];

  /**
   * Get all tracked entity IDs.
   */
  getTrackedEntities(): string[];

  /**
   * Get subscriber info for DevTools.
   */
  getSubscriptions(): ContentSubscription[];

  /**
   * Get conflict history.
   */
  getConflicts(): ContentConflict[];

  /**
   * Get merge decisions.
   */
  getMergeDecisions(): MergeDecision[];

  /**
   * Get a full DevTools snapshot.
   */
  getSnapshot(): ContentSyncSnapshot;

  /**
   * Set the default conflict strategy.
   */
  setConflictStrategy(strategy: ConflictStrategy): void;

  /**
   * Remove all data for an entity.
   */
  removeEntity(entityId: string): void;

  /**
   * Full reset (for testing).
   */
  reset(): void;
}

export const useContentSyncStore = create<ContentSyncState>((set, get) => ({
  entities: {},
  _handlers: {},
  _conflictHandlers: {},
  history: [],
  conflicts: [],
  mergeDecisions: [],
  subscriptions: [],
  conflictStrategy: 'last-writer-wins',

  publish(entityId, content, sourcePlugin, options) {
    const state = get();
    const existing = state.entities[entityId];
    const currentVersion = existing?.version ?? 0;
    const baseVersion = options?.baseVersion;
    const strategy = options?.conflictStrategy ?? state.conflictStrategy;

    let conflict = false;
    let merged = false;
    let resolvedContent = content;
    const accepted = true;

    // ── Conflict detection ──────────────────────────────────
    if (baseVersion !== undefined && baseVersion < currentVersion) {
      conflict = true;

      if (strategy === 'reject') {
        // Reject the update
        const conflictRecord: ContentConflict = {
          entityId,
          localVersion: currentVersion,
          incomingVersion: baseVersion,
          localContent: existing?.content ?? '',
          incomingContent: content,
          sourcePlugin,
          resolution: 'reject',
          resolvedContent: existing?.content ?? '',
          timestamp: Date.now(),
        };

        set((s) => ({
          conflicts: [...s.conflicts.slice(-(MAX_CONFLICTS - 1)), conflictRecord],
          mergeDecisions: [
            ...s.mergeDecisions.slice(-(MAX_MERGE_DECISIONS - 1)),
            {
              entityId,
              strategy: 'reject',
              sourcePlugin,
              baseVersion: currentVersion,
              resultVersion: currentVersion,
              timestamp: Date.now(),
              accepted: false,
            },
          ],
        }));

        // Emit conflict event via event bus
        _emitContentEvent('content.conflict', {
          entityId,
          sourcePlugin,
          localVersion: currentVersion,
          incomingVersion: baseVersion,
          resolution: 'reject',
        });

        // Notify conflict handlers
        _notifyConflictHandlers(get(), entityId, conflictRecord);

        invocationLog.append({
          kind: 'event-emit',
          pluginId: sourcePlugin,
          detail: { event: 'content.conflict', entityId, resolution: 'reject' },
        });

        return { accepted: false, version: currentVersion, conflict: true, merged: false, previousVersion: currentVersion };
      }

      if (strategy === 'merge' && options?.mergeFn) {
        resolvedContent = options.mergeFn(existing?.content ?? '', content);
        merged = true;
      }
      // 'last-writer-wins' — resolvedContent stays as `content`

      const conflictRecord: ContentConflict = {
        entityId,
        localVersion: currentVersion,
        incomingVersion: baseVersion,
        localContent: existing?.content ?? '',
        incomingContent: content,
        sourcePlugin,
        resolution: strategy,
        resolvedContent,
        timestamp: Date.now(),
      };

      set((s) => ({
        conflicts: [...s.conflicts.slice(-(MAX_CONFLICTS - 1)), conflictRecord],
      }));

      // Notify conflict handlers
      _notifyConflictHandlers(get(), entityId, conflictRecord);
    }

    // ── Create new version ──────────────────────────────────
    const newVersion = currentVersion + 1;
    const update: ContentVersion = {
      entityId,
      content: resolvedContent,
      version: newVersion,
      sourcePlugin,
      timestamp: Date.now(),
    };

    // ── Persist ─────────────────────────────────────────────
    set((s) => {
      const newHistory = [...s.history, update];
      return {
        entities: { ...s.entities, [entityId]: update },
        history: newHistory.length > MAX_TOTAL_HISTORY
          ? newHistory.slice(-MAX_TOTAL_HISTORY)
          : newHistory,
        mergeDecisions: [
          ...s.mergeDecisions.slice(-(MAX_MERGE_DECISIONS - 1)),
          {
            entityId,
            strategy: conflict ? strategy : 'last-writer-wins',
            sourcePlugin,
            baseVersion: currentVersion,
            resultVersion: newVersion,
            timestamp: Date.now(),
            accepted: true,
          },
        ],
      };
    });

    // ── Log to invocation log ───────────────────────────────
    invocationLog.append({
      kind: 'event-emit',
      pluginId: sourcePlugin,
      detail: {
        event: 'content.updated',
        entityId,
        version: newVersion,
        conflict,
        merged,
      },
    });

    // ── Emit events via plugin event bus ─────────────────────
    _emitContentEvent('content.updated', {
      entityId,
      content: resolvedContent,
      version: newVersion,
      sourcePlugin,
      previousVersion: currentVersion,
    });

    if (merged) {
      _emitContentEvent('content.merged', {
        entityId,
        sourcePlugin,
        baseVersion: currentVersion,
        resultVersion: newVersion,
        strategy,
      });
    }

    if (conflict) {
      _emitContentEvent('content.conflict', {
        entityId,
        sourcePlugin,
        localVersion: currentVersion,
        incomingVersion: baseVersion,
        resolution: strategy,
      });
    }

    // ── Notify subscribers (skip source plugin to avoid echo) ─
    const handlers = get()._handlers[entityId];
    if (handlers) {
      for (const [, entry] of handlers) {
        if (entry.subscriberPlugin === sourcePlugin) continue;
        try {
          entry.handler(update);
        } catch (err) {
          console.error(
            `[ContentSync] Handler error for ${entityId} in ${entry.subscriberPlugin}:`,
            err,
          );
        }
      }
    }

    return {
      accepted,
      version: newVersion,
      conflict,
      merged,
      previousVersion: currentVersion > 0 ? currentVersion : undefined,
    };
  },

  subscribe(entityId, handler, subscriberPlugin) {
    // Wrap handler with sandbox error boundary + slow detection
    const wrappedHandler = wrapSyncSubscriber(subscriberPlugin, entityId, handler);
    const subId = generateSubId();
    const sub: ContentSubscription = {
      id: subId,
      entityId,
      subscriberPlugin,
      createdAt: Date.now(),
    };

    set((s) => {
      const entityHandlers = s._handlers[entityId] ?? new Map();
      const newMap = new Map(entityHandlers);
      newMap.set(subId, { subscriberPlugin, handler: wrappedHandler });
      return {
        _handlers: { ...s._handlers, [entityId]: newMap },
        subscriptions: [...s.subscriptions, sub],
      };
    });

    // Return unsubscribe
    return () => {
      set((s) => {
        const entityHandlers = s._handlers[entityId];
        if (entityHandlers) {
          const newMap = new Map(entityHandlers);
          newMap.delete(subId);
          return {
            _handlers: { ...s._handlers, [entityId]: newMap },
            subscriptions: s.subscriptions.filter((ss) => ss.id !== subId),
          };
        }
        return {
          subscriptions: s.subscriptions.filter((ss) => ss.id !== subId),
        };
      });
    };
  },

  subscribeConflicts(entityId, handler, subscriberPlugin) {
    const subId = generateSubId();

    set((s) => {
      const entityHandlers = s._conflictHandlers[entityId] ?? new Map();
      const newMap = new Map(entityHandlers);
      newMap.set(subId, { subscriberPlugin, handler });
      return {
        _conflictHandlers: { ...s._conflictHandlers, [entityId]: newMap },
      };
    });

    return () => {
      set((s) => {
        const entityHandlers = s._conflictHandlers[entityId];
        if (entityHandlers) {
          const newMap = new Map(entityHandlers);
          newMap.delete(subId);
          return {
            _conflictHandlers: { ...s._conflictHandlers, [entityId]: newMap },
          };
        }
        return s;
      });
    };
  },

  getLatest(entityId) {
    return get().entities[entityId];
  },

  getHistory(entityId) {
    return get().history.filter((h) => h.entityId === entityId);
  },

  getTrackedEntities() {
    return Object.keys(get().entities);
  },

  getSubscriptions() {
    return [...get().subscriptions];
  },

  getConflicts() {
    return [...get().conflicts];
  },

  getMergeDecisions() {
    return [...get().mergeDecisions];
  },

  getSnapshot() {
    const s = get();
    return {
      entities: { ...s.entities },
      subscribers: [...s.subscriptions],
      history: [...s.history],
      conflicts: [...s.conflicts],
      mergeDecisions: [...s.mergeDecisions],
    };
  },

  setConflictStrategy(strategy) {
    set({ conflictStrategy: strategy });
  },

  removeEntity(entityId) {
    set((s) => {
      const newEntities = { ...s.entities };
      delete newEntities[entityId];
      const newHandlers = { ...s._handlers };
      delete newHandlers[entityId];
      const newConflictHandlers = { ...s._conflictHandlers };
      delete newConflictHandlers[entityId];
      return {
        entities: newEntities,
        _handlers: newHandlers,
        _conflictHandlers: newConflictHandlers,
        subscriptions: s.subscriptions.filter((sub) => sub.entityId !== entityId),
        history: s.history.filter((h) => h.entityId !== entityId),
      };
    });
  },

  reset() {
    set({
      entities: {},
      _handlers: {},
      _conflictHandlers: {},
      history: [],
      conflicts: [],
      mergeDecisions: [],
      subscriptions: [],
      conflictStrategy: 'last-writer-wins',
    });
  },
}));

// ────────────────────────────────────────────────────────
// Internal Helpers
// ────────────────────────────────────────────────────────

/**
 * Emit a content sync event through the plugin event bus.
 * Source is always 'content-sync' (host service).
 */
function _emitContentEvent(eventName: string, payload: Record<string, any>): void {
  try {
    usePluginEventBusStore.getState().emit('content-sync', eventName, payload);
  } catch (err) {
    console.error(`[ContentSync] Failed to emit ${eventName}:`, err);
  }
}

/**
 * Notify conflict handlers for a specific entity.
 */
function _notifyConflictHandlers(
  state: ContentSyncState,
  entityId: string,
  conflict: ContentConflict,
): void {
  const handlers = state._conflictHandlers[entityId];
  if (!handlers) return;
  for (const [, entry] of handlers) {
    try {
      entry.handler(conflict);
    } catch (err) {
      console.error(
        `[ContentSync] Conflict handler error for ${entityId} in ${entry.subscriberPlugin}:`,
        err,
      );
    }
  }
}

// ────────────────────────────────────────────────────────
// Plugin-Scoped API Factory
// ────────────────────────────────────────────────────────

/**
 * Plugin-facing content sync API.
 * Scoped to a specific plugin — the plugin never specifies its own
 * sourcePlugin; the host injects it.
 */
export interface ContentSyncAPI {
  /** Publish content for an entity. Host manages versioning. */
  publish(
    entityId: string,
    content: string,
    options?: {
      baseVersion?: number;
      conflictStrategy?: ConflictStrategy;
      mergeFn?: (existing: string, incoming: string) => string;
    },
  ): PublishResult;

  /** Subscribe to content updates for an entity. */
  subscribe(entityId: string, handler: ContentUpdateHandler): () => void;

  /** Subscribe to conflict notifications for an entity. */
  subscribeConflicts(entityId: string, handler: ContentConflictHandler): () => void;

  /** Get the latest version of an entity. */
  getLatest(entityId: string): ContentVersion | undefined;

  /** Get version history for an entity. */
  getHistory(entityId: string): ContentVersion[];
}

/**
 * Create a content sync API scoped to a specific plugin.
 * Used by the host-ui-api factory to inject into plugin contexts.
 */
export function createContentSyncAPI(pluginId: string): ContentSyncAPI {
  const store = useContentSyncStore;

  return {
    publish(entityId, content, options) {
      invocationLog.append({
        kind: 'event-emit',
        pluginId,
        detail: { event: 'contentSync.publish', entityId },
      });
      return store.getState().publish(entityId, content, pluginId, options);
    },

    subscribe(entityId, handler) {
      return store.getState().subscribe(entityId, handler, pluginId);
    },

    subscribeConflicts(entityId, handler) {
      return store.getState().subscribeConflicts(entityId, handler, pluginId);
    },

    getLatest(entityId) {
      return store.getState().getLatest(entityId);
    },

    getHistory(entityId) {
      return store.getState().getHistory(entityId);
    },
  };
}
